(() => {
  function registerProductContentFeatures() {
    const theme = window.KSTheme;

    if (!theme?.registerFeature) return;

    const { collect } = theme;
    const body = document.body;

  function getProductContentRoot(section) {
    const source = section?.querySelector?.('[data-product-content-source]');
    const markup = String(source?.innerHTML || '').trim();

    if (!markup) return null;

    const parsedDocument = new DOMParser().parseFromString(`<div data-product-content-root>${markup}</div>`, 'text/html');

    return parsedDocument.body.firstElementChild;
  }

  function hasMeaningfulContent(node) {
    if (!node) return false;

    if (node.querySelector('img, video, iframe, table, ul, ol, p, blockquote, h1, h2, h3, h4, h5, h6')) {
      return true;
    }

    return String(node.textContent || '').replace(/\s+/g, ' ').trim() !== '';
  }

  function normalizeDescriptionClass(value) {
    return String(value || '')
      .trim()
      .replace(/^\./, '')
      .split(/\s+/)[0];
  }

  function extractTargetedDescriptionHtml(section, targetClass) {
    const normalizedClass = normalizeDescriptionClass(targetClass);

    if (!normalizedClass) return '';

    const contentRoot = getProductContentRoot(section);
    if (!contentRoot) return '';

    const targetNode = Array.from(contentRoot.querySelectorAll('[class]'))
      .find((node) => node.classList.contains(normalizedClass));

    return targetNode && hasMeaningfulContent(targetNode) ? targetNode.outerHTML.trim() : '';
  }

  function buildSharedGuideTable(source) {
    const raw = String(source.textContent || '').trim();
    const headers = source.dataset.sizeGuideTableSource === 'body'
      ? ['Size', 'Chest Apex', 'Waist', 'Fullest Hip'] : ['Size', 'Numeric Size'];
    if (!raw || raw.length > 100000) return null;
    let rows;
    if (/<table[\s>]/i.test(raw)) {
      // Template content stays inert. Never insert merchant HTML into the live DOM.
      const parsed = document.createElement('template');
      parsed.innerHTML = raw;
      parsed.content.querySelectorAll('script, style, iframe, object, embed, svg, math, template').forEach((node) => node.remove());
      const table = parsed.content.querySelector('table');
      if (!table) return null;
      rows = Array.from(table.rows).filter((row) => row.closest('table') === table)
        .map((row) => Array.from(row.cells).map((cell) => cell.textContent.replace(/\s+/g, ' ').trim()));
    } else {
      // Accept the earlier plain-text row format for existing entries.
      rows = [headers, ...raw.split(/\r?\n/).filter((row) => row.trim()).map((row) => row.split('|').map((cell) => cell.trim()))];
    }
    // Women's conversion has two columns; unisex conversion adds women's size.
    // Keep rectangular text-only tables and reject malformed or oversized data.
    const columnCount = rows[0]?.length;
    const validColumnCount = source.dataset.sizeGuideTableSource === 'body'
      ? columnCount === 4 : columnCount === 2 || columnCount === 3;
    if (!validColumnCount || rows.length < 2 || rows.length > 101 || rows.some((row) => row.length !== columnCount || row.some((cell) => !cell))) return null;
    const table = document.createElement('table');
    table.className = 'ks-size-guide__table';
    const caption = table.createCaption();
    caption.textContent = source.dataset.sizeGuideTableSource === 'body' ? 'Body Measurements (in inches)' : 'Size comparison';
    const head = table.createTHead();
    const body = table.createTBody();
    rows.forEach((row, rowIndex) => {
      const tr = (rowIndex === 0 ? head : body).insertRow();
      row.forEach((value, columnIndex) => {
        const cell = document.createElement(rowIndex === 0 || columnIndex === 0 ? 'th' : 'td');
        if (rowIndex === 0) cell.scope = 'col';
        else if (columnIndex === 0) cell.scope = 'row';
        cell.textContent = value;
        tr.appendChild(cell);
      });
    });
    return table;
  }

  function initSizeGuides(scope = document) {
    collect(scope, '[data-product-section-id]').forEach((section) => {
      if (section.dataset.sizeGuideInitialized === 'true') return;
      section.dataset.sizeGuideInitialized = 'true';

      const sectionId = section.dataset.productSectionId || section.id || 'product';
      const dialogId = `SizeGuide-${sectionId}`;
      const existingDialog = document.getElementById(dialogId);

      if (existingDialog) {
        existingDialog.remove();
      }

      const triggers = Array.from(section.querySelectorAll('[data-size-guide-trigger]'));
      const helpers = Array.from(section.querySelectorAll('[data-fit-guide-helper]'));

      if (!triggers.length) return;

      helpers.forEach((helper) => { helper.hidden = true; });
      triggers.forEach((trigger) => {
        trigger.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.removeAttribute('aria-controls');
      });

      const sharedGuide = section.querySelector('template[data-shared-size-guide]');
      if (!sharedGuide) return;
      const guideContent = sharedGuide.content.cloneNode(true);
      const sharedPanels = Array.from(guideContent.querySelectorAll('[data-size-guide-tab-content]'));
      if (sharedPanels.length !== 2) return;
      for (const sharedPanel of sharedPanels) {
        const source = sharedPanel.querySelector('[data-size-guide-table-source]');
        const table = source && buildSharedGuideTable(source);
        if (!table) return;
        source.replaceWith(table);
      }

      const dialog = document.createElement('dialog');
      const panel = document.createElement('div');
      const header = document.createElement('div');
      const title = document.createElement('h2');
      const closeButton = document.createElement('button');
      const bodyContent = document.createElement('div');

      dialog.id = dialogId;
      dialog.className = 'ks-size-guide';
      dialog.setAttribute('data-size-guide-dialog', '');
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', `${dialogId}-title`);

      panel.className = 'ks-size-guide__panel';
      panel.setAttribute('data-size-guide-panel', '');

      header.className = 'ks-size-guide__header';

      title.className = 'ks-size-guide__title';
      title.id = `${dialogId}-title`;
      title.textContent = sharedGuide.dataset.guideTitle || 'Size Guide';

      closeButton.type = 'button';
      closeButton.className = 'ks-size-guide__close ks-ui-close';
      closeButton.setAttribute('aria-label', 'Close size guide');
      closeButton.innerHTML = '<svg class="ks-ui-close__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>';

      bodyContent.className = 'ks-size-guide__body';
      header.append(title, closeButton);

      if (sharedGuide) {
        dialog.classList.add('ks-size-guide--shared');
        const panels = Array.from(guideContent.querySelectorAll('[data-size-guide-tab-content]'));
        const tabList = document.createElement('div');
        tabList.className = 'ks-size-guide__tabs';
        tabList.setAttribute('role', 'tablist');
        tabList.setAttribute('aria-label', title.textContent);
        const tabs = panels.map((tabPanel, index) => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'ks-size-guide__tab';
          tab.id = `${dialogId}-tab-${index}`;
          tab.textContent = tabPanel.dataset.label;
          tab.setAttribute('role', 'tab');
          tabPanel.id = `${dialogId}-panel-${index}`;
          tabPanel.classList.add('ks-size-guide__tabpanel');
          tabPanel.setAttribute('role', 'tabpanel');
          tabPanel.setAttribute('aria-labelledby', tab.id);
          tab.setAttribute('aria-controls', tabPanel.id);
          tabList.appendChild(tab);
          return tab;
        });
        function activateTab(activeIndex, moveFocus = false) {
          tabs.forEach((tab, index) => {
            const active = index === activeIndex;
            tab.setAttribute('aria-selected', String(active));
            tab.tabIndex = active ? 0 : -1;
            panels[index].hidden = !active;
          });
          if (moveFocus) tabs[activeIndex].focus();
        }
        tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => activateTab(index));
          tab.addEventListener('keydown', (event) => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = tabs.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            activateTab(next, true);
          });
        });
        activateTab(0);
        bodyContent.append(tabList, guideContent);
      }

      panel.append(header, bodyContent);
      dialog.appendChild(panel);
      body.appendChild(dialog);

      function closeGuide() {
        if (!dialog.open) return;
        dialog.close();
      }

      helpers.forEach((helper) => { helper.hidden = false; });
      triggers.forEach((trigger) => {
        trigger.hidden = false;
        trigger.setAttribute('aria-controls', dialogId);
        trigger.setAttribute('aria-expanded', 'false');

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          dialog.__returnFocusEl = trigger;
          trigger.setAttribute('aria-expanded', 'true');

          if (!dialog.open) dialog.showModal();
        });
      });

      closeButton.addEventListener('click', closeGuide);

      dialog.addEventListener('click', (event) => {
        if (!panel.contains(event.target)) {
          closeGuide();
        }
      });

      dialog.addEventListener('close', () => {
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

        if (dialog.__returnFocusEl && dialog.__returnFocusEl.isConnected) {
          dialog.__returnFocusEl.focus({ preventScroll: true });
        }

        dialog.__returnFocusEl = null;
      });
    });
  }

  function initProductContentBlocks(scope = document) {
    collect(scope, '[data-product-section-id]').forEach((section) => {
      const descriptionTargets = collect(section, '[data-product-description-target]');

      descriptionTargets.forEach((descriptionTarget) => {
        const descriptionBlock = descriptionTarget.closest('[data-product-description-block]');
        const descriptionWrapper = descriptionTarget.closest('.ks-product__block');
        const descriptionHtml = extractTargetedDescriptionHtml(
          section,
          descriptionTarget.dataset.productDescriptionClass,
        );

        if (descriptionHtml) {
          descriptionTarget.innerHTML = descriptionHtml;
          descriptionTarget.hidden = false;
          if (descriptionBlock) descriptionBlock.hidden = false;
          if (descriptionWrapper) descriptionWrapper.hidden = false;
        } else {
          descriptionTarget.innerHTML = '';
          descriptionTarget.hidden = true;
          const fitHelper = descriptionBlock?.querySelector('[data-fit-guide-helper]');
          if (fitHelper && !fitHelper.hidden) {
            descriptionBlock.hidden = false;
            if (descriptionWrapper) descriptionWrapper.hidden = false;
            return;
          }
          if (descriptionBlock) {
            descriptionBlock.hidden = true;
            descriptionBlock.open = false;
          }
          if (descriptionWrapper) descriptionWrapper.hidden = true;
        }
      });

      collect(section, '[data-fit-guide-only]').forEach((block) => {
        const helper = block.querySelector('[data-fit-guide-helper]');
        if (helper?.hidden) {
          block.hidden = true;
          const wrapper = block.closest('.ks-product__block');
          if (wrapper) wrapper.hidden = true;
        }
      });

    });
  }


    theme.registerFeature('product-content-features', (scope = document) => {
      initSizeGuides(scope);
      initProductContentBlocks(scope);
    });
  }

  if (window.KSTheme?.registerFeature) {
    registerProductContentFeatures();
  } else {
    document.addEventListener('ks:theme:ready', registerProductContentFeatures, { once: true });
  }
})();
